/**
 * Character Bible Service
 * 
 * Provides utilities for building the Identity Lock Header from a CharacterBible
 * and debug logging to prevent character drift across story pages.
 */

import { CharacterBible, CharacterDefinition } from '../types';
import { debugLog } from '../utils/debugLogger';

/**
 * Generates a short SHA-256 hash of the CharacterBible for tracking consistency.
 * Returns first 12 characters of the hash.
 */
export async function hashCharacterBible(bible: CharacterBible): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(bible));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex.slice(0, 12);
}

/**
 * Summarizes a character's appearance for the identity header.
 */
function summarizeAppearance(char: CharacterDefinition): string {
    const parts: string[] = [];

    if (char.appearance.coat_or_skin) {
        parts.push(char.appearance.coat_or_skin);
    }
    if (char.appearance.color_palette) {
        parts.push(char.appearance.color_palette);
    }

    return parts.join(', ') || 'not specified';
}

/**
 * Formats a character definition for inclusion in the Identity Lock Header.
 */
function formatCharacterForHeader(char: CharacterDefinition, isMain: boolean): string {
    const typeInfo = [
        char.type,
        char.species ? `species: ${char.species}` : null,
        char.breed ? `breed: ${char.breed}` : null,
        char.age ? `age: ${char.age}` : null,
    ].filter(Boolean).join(', ');

    const marks = char.appearance.distinctive_marks.length > 0
        ? char.appearance.distinctive_marks.join(', ')
        : 'none';

    const accessories = char.appearance.clothing_or_accessories.length > 0
        ? char.appearance.clothing_or_accessories.join(', ')
        : 'none';

    const hardRules = char.do_not_change.length > 0
        ? char.do_not_change.join('; ')
        : 'maintain all locked traits';

    if (isMain) {
        return `Main character: ${char.name}. Type: ${typeInfo}.
Core appearance: ${summarizeAppearance(char)}.
Distinctive marks: ${marks}.
Accessories: ${accessories}.
Hard rules: ${hardRules}. Never change species/breed. Never turn an animal into a human.`;
    }

    return `${char.name} (${typeInfo}): ${summarizeAppearance(char)}. Marks: ${marks}. Rules: ${hardRules}.`;
}

/**
 * Builds the Identity Lock Header from a CharacterBible.
 * This header MUST be prepended to every per-page generation prompt
 * (both text and image prompts) to prevent character drift.
 */
export function buildIdentityLockHeader(bible: CharacterBible): string {
    const mainCharacter = formatCharacterForHeader(bible.main, true);

    const supportingSummary = bible.supporting.length > 0
        ? bible.supporting.map(c => formatCharacterForHeader(c, false)).join('\n')
        : 'None defined.';

    const cameraRules = bible.style_lock.camera_rules.length > 0
        ? bible.style_lock.camera_rules.join('; ')
        : 'maintain consistent framing';

    const consistencyRules = bible.style_lock.consistency_rules.length > 0
        ? bible.style_lock.consistency_rules.join('; ')
        : 'maintain visual consistency throughout';

    return `CHARACTER CONSISTENCY LOCK (do not override):
${mainCharacter}
Supporting characters (locked): ${supportingSummary}
Style lock: ${bible.style_lock.art_style}. Camera: ${cameraRules}. Consistency: ${consistencyRules}.
If any later text conflicts with locked traits, ignore the conflict and keep the locked traits.`;
}

/**
 * Debug logs the CharacterBible with hash and header preview.
 * Does NOT log full user prompts or PII.
 */
export async function debugLogCharacterBible(bible: CharacterBible): Promise<void> {
    const hash = await hashCharacterBible(bible);
    const header = buildIdentityLockHeader(bible);
    const headerPreview = header.slice(0, 200) + (header.length > 200 ? '...' : '');

    debugLog("CHARBIBLE", "Character Bible Created", {
        hash,
        headerPreview,
        mainCharacterName: bible.main.name,
        supportingCount: bible.supporting.length,
    });
}

/**
 * Validates that a prompt contains the Identity Lock Header.
 * Throws an error if validation fails.
 */
export function assertIdentityHeaderPresent(prompt: string, context: string): void {
    if (!prompt.includes('CHARACTER CONSISTENCY LOCK')) {
        throw new Error(`[CharacterBible] Identity Lock Header missing from ${context}. This is a bug.`);
    }
}
